import React, { useState } from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Separator } from './ui/separator';
import SEO from './SEO';
import { 
  Users,
  FileText,
  GraduationCap,
  Shield,
  TrendingUp,
  Handshake,
  Brain,
  CheckCircle,
  AlertCircle,
  Loader2,
  Sparkles,
  Target,
  Clock,
  Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRConsultancyPage = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    companyWebsite: '',
    serviceDetails: ''
  });
  
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${BACKEND_URL}/api/hr-consultancy/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: formData.companyName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          company_website: formData.companyWebsite,
          service_details: formData.serviceDetails
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFormStatus({
          type: 'success',
          message: 'Thank you for your inquiry! Our HR consultancy team will contact you within 24 hours.'
        });
        // Reset form
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          companyWebsite: '',
          serviceDetails: ''
        });
      } else {
        setFormStatus({
          type: 'error',
          message: data.detail || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Failed to submit form. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hrServices = [
    {
      icon: Users,
      title: "AI-Supported Employee Relations Management",
      description: "End-to-end support for all ER cases through human expertise combined with automation.",
      features: [
        "Automated case tracking and document generation",
        "AI-guided disciplinary and grievance workflows",
        "Support for complex and high-risk investigations",
        "Intelligent policy interpretation and compliance prompts"
      ],
      outcome: "Faster, more consistent ER decisions with reduced risk.",
      gradient: "from-blue-500/20 to-blue-500/5",
      iconColor: "text-blue-600"
    },
    {
      icon: Target,
      title: "HR Process Optimisation & Automation",
      description: "Streamline and modernise HR workflows using AI-powered systems.",
      features: [
        "Automation of HR processes to remove manual workload",
        "Continuous improvement powered by AI insights",
        "Best-practice frameworks embedded into workflows",
        "Smart dashboards for visibility and reporting"
      ],
      outcome: "Reduced admin time, improved accuracy, and better compliance.",
      gradient: "from-green-500/20 to-green-500/5",
      iconColor: "text-green-600"
    },
    {
      icon: GraduationCap,
      title: "Training & Leadership Development with AI Coaching",
      description: "Empower managers through expert training and AI-led micro-coaching.",
      features: [
        "Leadership training for ER procedures",
        "AI-assisted scenario practice for grievance handling",
        "Manager coaching for fair decision-making",
        "Automated training insights and follow-ups"
      ],
      outcome: "Confident managers and fewer escalated issues.",
      gradient: "from-purple-500/20 to-purple-500/5",
      iconColor: "text-purple-600"
    },
    {
      icon: FileText,
      title: "Policy, Documentation & AI-Driven Compliance",
      description: "Modernise and maintain your organisation's HR documentation.",
      features: [
        "Review and refresh existing HR policies",
        "AI-assisted template creation",
        "Automated checks for legal alignment",
        "Digital policy hubs with instant retrieval"
      ],
      outcome: "Updated, accessible HR governance with automated compliance.",
      gradient: "from-orange-500/20 to-orange-500/5",
      iconColor: "text-orange-600"
    },
    {
      icon: TrendingUp,
      title: "Change Management & Redundancy Support",
      description: "Smart AI-enabled change frameworks for smooth transitions.",
      features: [
        "Strategy development for organisational change",
        "Legally compliant redundancy process automation",
        "AI-guided communication planning",
        "Engagement tracking and sentiment insights"
      ],
      outcome: "Smooth transitions, protected morale, and reduced legal exposure.",
      gradient: "from-red-500/20 to-red-500/5",
      iconColor: "text-red-600"
    },
    {
      icon: Handshake,
      title: "Union Engagement & Workforce Consultation Support",
      description: "Enhanced union interactions with structured support tools.",
      features: [
        "AI-organised consultation timelines",
        "Documented negotiation frameworks",
        "Data-driven insights for leadership",
        "Support for HR projects aligned with goals"
      ],
      outcome: "More constructive union relationships and transparent consultation.",
      gradient: "from-indigo-500/20 to-indigo-500/5",
      iconColor: "text-indigo-600"
    }
  ];

  const whyChooseUs = [
    { icon: Award, text: "20+ years of senior HR leadership experience" },
    { icon: Shield, text: "Expertise in complex ER, union engagement, and organisational change" },
    { icon: Sparkles, text: "Smart AI automation that accelerates HR operations" },
    { icon: Handshake, text: "Strategic partnership with leadership teams" },
    { icon: CheckCircle, text: "Empathetic, fair, compliance-driven approach" },
    { icon: Target, text: "Proven results aligned with organisational goals" }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEO 
        title="HR Consultancy Services - Smart AI Solutions"
        description="Expert HR consultancy combining 20+ years of leadership experience with AI-powered automation for Employee Relations, compliance, change management, and strategic HR support."
        keywords="HR consultancy, employee relations, AI automation, HR compliance, change management, HR leadership"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1544717297-fa95b6ee9643"
            alt="Professional HR Consultancy"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative container mx-auto px-6 py-20 lg:py-28">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-8">
              <Brain className="h-5 w-5 text-[#ff7a1f]" />
              <span className="text-sm font-semibold">AI-Enhanced HR Expertise</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Smart AI Solutions – <span className="text-[#ff7a1f]">HR Consultancy Service Suite</span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed">
              Over 20 years of HR leadership expertise combined with advanced AI-powered systems to deliver next-generation HR consultancy
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#112d56] font-semibold px-8 py-6 text-lg rounded-xl"
                onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Request a Consultation
              </Button>
              <Button 
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#ff6a0f] text-white font-semibold px-8 py-6 text-lg rounded-xl"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Our Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Our Expertise */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#112d56] mb-4">
                About Our <span className="text-[#ff7a1f]">HR Expertise</span>
              </h2>
            </div>
            
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 lg:p-12">
                <div className="relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_smart-ai-rebrand/artifacts/x5k831f4_a-professional-photograph-of-a-modern-hr_qF_cyBmGTeSdFHTyjXIq4w__DBCI7X_TfyGf3pzsHpq2Q.png"
                    alt="Professional HR Consultation"
                    className="float-left w-full md:w-1/2 lg:w-5/12 mr-0 md:mr-8 mb-6 rounded-lg shadow-lg object-cover"
                  />
                  
                  <div className="text-content">
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      With <strong className="text-[#112d56]">two decades of experience</strong> in HR leadership, Employee Relations, change initiatives, and organisational transformation, we deliver AI-enhanced HR support that blends:
                    </p>
                    
                    <ul className="space-y-3 mb-6">
                      {[
                        "Deep technical HR knowledge",
                        "Hands-on operational experience",
                        "Automated workflows and decision-support",
                        "Strategic insight for leadership teams"
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="h-6 w-6 text-[#ff7a1f] flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 font-medium">{item}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      Our approach ensures <strong className="text-[#112d56]">fairness, compliance, and business alignment</strong> while improving efficiency through Smart AI technology.
                    </p>
                    
                    <p className="text-lg text-gray-700 leading-relaxed">
                      We combine the wisdom of hands-on HR leadership with the precision of AI-powered systems to deliver consultancy services that are both strategically sound and operationally efficient. From complex employee relations cases to large-scale organisational change, we provide the expertise and technology your business needs to succeed.
                    </p>
                  </div>
                  
                  <div className="clear-both"></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Our Smart HR <span className="text-[#ff7a1f]">Consultancy Services</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Comprehensive HR solutions combining strategic human insight with intelligent automation
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {hrServices.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300">
                    <CardHeader className="pb-6">
                      <div className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-4`}>
                        <IconComponent className={`h-8 w-8 ${service.iconColor}`} />
                      </div>
                      <CardTitle className="text-2xl font-bold text-[#112d56] mb-3">{service.title}</CardTitle>
                      <CardDescription className="text-gray-600 leading-relaxed text-base mb-4">
                        {service.description}
                      </CardDescription>
                      
                      <div className="space-y-2 mb-4">
                        {service.features.map((feature, featureIdx) => (
                          <div key={featureIdx} className="flex items-start space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-gradient-to-r from-[#ff7a1f]/10 to-[#ff7a1f]/5 p-4 rounded-lg border-l-4 border-[#ff7a1f]">
                        <p className="text-sm font-semibold text-[#112d56]">
                          <strong>Outcome:</strong> {service.outcome}
                        </p>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Leadership Support */}
      <section className="py-16 bg-gradient-to-br from-[#112d56] to-[#1a3d6b] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <Brain className="h-16 w-16 text-[#ff7a1f] mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Strategic Leadership Support</h2>
            <p className="text-xl text-blue-100 leading-relaxed mb-8">
              With leadership-level experience, we offer intelligent guidance for boards and executive teams:
            </p>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              {[
                "AI-supported people-impact analysis for business decisions",
                "Culture transformation insights and implementation plans",
                "HR strategy development aligned with organisational objectives",
                "Trusted advisory to influence senior decision-making"
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-[#ff7a1f] flex-shrink-0 mt-0.5" />
                  <span className="text-lg">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-xl font-semibold text-[#ff7a1f] mt-8">
              Outcome: Stronger strategic alignment and better organisational outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#112d56] mb-4">
                Why Organisations Choose <span className="text-[#ff7a1f]">Smart AI Solutions</span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyChooseUs.map((item, idx) => {
                const IconComponent = item.icon;
                return (
                  <Card key={idx} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="h-6 w-6 text-[#ff7a1f]" />
                      </div>
                      <p className="text-gray-700 font-medium leading-relaxed">{item.text}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <div className="text-center mt-12">
              <p className="text-xl text-gray-700 font-semibold">
                Smart AI Solutions delivers HR consultancy that is <span className="text-[#ff7a1f]">faster, more accurate, and strategically aligned</span>, giving your organisation the advantage of human insight reinforced with intelligent automation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#112d56] mb-4">
                Get in Touch with Our <span className="text-[#ff7a1f]">HR Consultancy Team</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Tell us about your HR needs and we'll respond within 24 hours
              </p>
            </div>
            
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-8 lg:p-12">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff7a1f] focus:border-transparent"
                        placeholder="Your company name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="contactName" className="block text-sm font-semibold text-gray-700 mb-2">
                        Contact Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contactName"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff7a1f] focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff7a1f] focus:border-transparent"
                        placeholder="your.email@company.com"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff7a1f] focus:border-transparent"
                        placeholder="+44 123 456 7890"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="companyWebsite" className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Website
                    </label>
                    <input
                      type="url"
                      id="companyWebsite"
                      name="companyWebsite"
                      value={formData.companyWebsite}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff7a1f] focus:border-transparent"
                      placeholder="https://www.yourcompany.com"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="serviceDetails" className="block text-sm font-semibold text-gray-700 mb-2">
                      HR Service Requirements <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="serviceDetails"
                      name="serviceDetails"
                      value={formData.serviceDetails}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff7a1f] focus:border-transparent"
                      placeholder="Please describe the kind of HR consultancy services you require, any specific challenges you're facing, and your timeline..."
                    />
                  </div>
                  
                  {formStatus.message && (
                    <div className={`p-4 rounded-lg flex items-start space-x-3 ${
                      formStatus.type === 'success' 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      {formStatus.type === 'success' ? (
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <p className={`text-sm ${
                        formStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {formStatus.message}
                      </p>
                    </div>
                  )}
                  
                  <div className="text-center">
                    <Button 
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="bg-[#ff7a1f] hover:bg-[#ff6a0f] text-white font-semibold px-12 py-6 text-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Inquiry'
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vioq-content-update/artifacts/w40y3694_Smart%20AI%20Logo%20white.png"
                  alt="Smart AI Solutions"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                AI-powered voice & knowledge solutions for modern businesses. Driving efficiency and customer excellence.
              </p>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/in/smataisolutionstech/" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-[#ff7a1f] transition-colors">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href="https://www.youtube.com/channel/UCij66Z3ffQtVIw3aIqNVN0g" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-[#ff7a1f] transition-colors">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">VioSuite Products</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/products/vioqbase" className="hover:text-[#ff7a1f] transition-colors text-lg">VioQbase</a></li>
                <li><a href="/products/vioreceptionist" className="hover:text-[#ff7a1f] transition-colors text-lg">VioReceptionist</a></li>
                <li><a href="/products/vioconcierge" className="hover:text-[#ff7a1f] transition-colors text-lg">VioConcierge</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Solutions</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/hr-consultancy" className="hover:text-[#ff7a1f] transition-colors text-lg">HR Consultancy</a></li>
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-blue-800/50 pt-8">
            <div className="text-center">
              <p className="text-blue-200 text-lg">
                © 2025 Smart AI Solutions. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HRConsultancyPage;